import { logout, refreshToken } from "./../redux/AuthSlice";
import axios from "axios";
import { AppStore } from "../redux/store";

//const BASE_URL = "https://staging-portal.wwsg.ge/api";

const BASE_URL = "https://api.mc-transit.world";

export const publicRequest = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

// The API builds media links with http://, which the server answers with 503;
// switch them to https until the backend is fixed
const API_HTTP = BASE_URL.replace("https://", "http://");

const toHttps = (value: any): any => {
  if (typeof value === "string") {
    return value.startsWith(API_HTTP) ? BASE_URL + value.slice(API_HTTP.length) : value;
  }
  if (Array.isArray(value)) return value.map(toHttps);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, v]) => [key, toHttps(v)])
    );
  }
  return value;
};

publicRequest.interceptors.response.use((response) => {
  response.data = toHttps(response.data);
  return response;
});

export const userRequest = axios.create({
  baseURL: BASE_URL,
});

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

const setupInterceptors = (store: AppStore) => {
  userRequest.interceptors.request.use(
    (config) => {
      const state = store.getState();
      const accessToken = state.auth.access;

      if (accessToken) {
        config.headers["Authorization"] = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  userRequest.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      const isTokenError =
        error.response?.status === 401 &&
        error.response?.data?.code === "token_not_valid" &&
        error.response?.data?.messages?.[0]?.token_class === "AccessToken";

      if (isTokenError && !originalRequest._retry) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({
              resolve: (token: string) => {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;

                if (originalRequest._formDataBackup) {
                  originalRequest.data = originalRequest._formDataBackup;
                }

                resolve(userRequest(originalRequest));
              },
              reject: (err: any) => reject(err),
            });
          });
        }

        isRefreshing = true;

        try {
          const result = await store.dispatch(refreshToken());

          if (refreshToken.fulfilled.match(result)) {
            const newAccessToken = result.payload.access;

            processQueue(null, newAccessToken);
            originalRequest.headers[
              "Authorization"
            ] = `Bearer ${newAccessToken}`;

            if (originalRequest._formDataBackup) {
              originalRequest.data = originalRequest._formDataBackup;
            }

            return userRequest(originalRequest);
          } else {
            processQueue(new Error("Refresh failed"), null);
            await store.dispatch(logout());
            window.location.href = "/login";
            return Promise.reject(error);
          }
        } catch (err) {
          processQueue(err, null);
          await store.dispatch(logout());
          window.location.href = "/login";
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};
export { setupInterceptors };
